/**
 * ProductHierarchy Page
 *
 * Three-panel Miller Columns UI:
 *   [Packages] → [Commercial Products] → [Technical Products]
 *
 * Panel 2 loads all commercial products on mount — no package required.
 * Selecting a package overlays linked/available grouping on Panel 2.
 * Selecting a commercial product loads Panel 3.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import hierarchyService from '../services/hierarchyService';
import api from '../services/api';
import PackagesPanel from '../components/ProductHierarchy/PackagesPanel';
import CommercialProductsPanel from '../components/ProductHierarchy/CommercialProductsPanel';
import TechnicalProductsPanel from '../components/ProductHierarchy/TechnicalProductsPanel';
import ProductForm from './ProductForm';
import { USER_ROLES } from '../config/constants';
import './ProductHierarchy.css';

const ProductHierarchy = () => {
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    api.getCurrentUser()
      .then((res) => {
        const role = res.data?.data?.user?.role;
        setIsAdmin(role === USER_ROLES.ADMIN);
      })
      .catch(() => {
        if (window.location.hostname === 'localhost') setIsAdmin(true);
      });
  }, []);

  // Panel 1 state
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Panel 2 state — allCommercial is always loaded; linked/available overlay when package selected
  const [allCommercial, setAllCommercial] = useState([]);
  const [linkedCommercial, setLinkedCommercial] = useState([]);
  const [availableCommercial, setAvailableCommercial] = useState([]);
  const [commercialLoading, setCommercialLoading] = useState(true);
  const [selectedCommercial, setSelectedCommercial] = useState(null);

  // Panel 3 state
  const [technicalProducts, setTechnicalProducts] = useState(null);
  const [technicalLoading, setTechnicalLoading] = useState(false);
  const [technicalSaving, setTechnicalSaving] = useState(false);
  const [technicalDirty, setTechnicalDirty] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalProductType, setModalProductType] = useState(null);

  // Toast notification
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Load all commercial products (Panel 2 baseline — no package needed)
  const loadAllCommercial = useCallback(async () => {
    try {
      setCommercialLoading(true);
      const res = await hierarchyService.getAllCommercialProducts();
      setAllCommercial(res.data.data.products || []);
    } catch (err) {
      console.error('Error loading commercial products:', err);
      showToast('Failed to load commercial products', 'error');
    } finally {
      setCommercialLoading(false);
    }
  }, []);

  // Load packages (Panel 1)
  const loadPackages = useCallback(async () => {
    try {
      setPackagesLoading(true);
      const res = await hierarchyService.getPackages();
      setPackages(res.data.data.packages || []);
    } catch (err) {
      console.error('Error loading packages:', err);
      showToast('Failed to load packages', 'error');
    } finally {
      setPackagesLoading(false);
    }
  }, []);

  // On mount — load both panels independently
  useEffect(() => {
    loadPackages();
    loadAllCommercial();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset all selections and reload both panels
  const handleRefresh = () => {
    setSelectedPackage(null);
    setLinkedCommercial([]);
    setAvailableCommercial([]);
    setSelectedCommercial(null);
    setTechnicalProducts(null);
    setTechnicalDirty(false);
    preSelectApplied.current = false;
    loadPackages();
    loadAllCommercial();
  };

  // Pre-select products when navigated here from the dashboard link icon
  const preSelectApplied = useRef(false);
  useEffect(() => {
    if (preSelectApplied.current) return;
    if (packagesLoading || commercialLoading) return;

    const state = location.state;
    if (!state?.preSelectCommercialId && !state?.preSelectPackageId) return;

    preSelectApplied.current = true;

    if (state.preSelectPackageId) {
      const pkg = packages.find(p => p.id === state.preSelectPackageId);
      if (pkg) {
        setSelectedPackage(pkg);
        loadCommercialForPackage(pkg);
      }
    }

    if (state.preSelectCommercialId) {
      const commercial = allCommercial.find(c => c.id === state.preSelectCommercialId);
      if (commercial) {
        setSelectedCommercial(commercial);
        loadTechnical(commercial);
      }
    }
  }, [packagesLoading, commercialLoading, packages, allCommercial, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load linked/available split when a package is selected
  const loadCommercialForPackage = useCallback(async (pkg) => {
    try {
      setCommercialLoading(true);
      const res = await hierarchyService.getCommercialProductsForPackage(pkg.id);
      const { linked, available } = res.data.data;
      setLinkedCommercial(linked || []);
      setAvailableCommercial(available || []);
    } catch (err) {
      console.error('Error loading commercial products for package:', err);
      showToast('Failed to load commercial products', 'error');
    } finally {
      setCommercialLoading(false);
    }
  }, []);

  const handleSelectPackage = (pkg) => {
    if (technicalDirty) {
      // eslint-disable-next-line no-alert
      if (!window.confirm('You have unsaved changes. Save or discard before switching.')) return;
      setTechnicalDirty(false);
    }
    // Toggle: clicking the already-selected package deselects it
    if (selectedPackage?.id === pkg.id) {
      setSelectedPackage(null);
      setLinkedCommercial([]);
      setAvailableCommercial([]);
      return;
    }
    setSelectedPackage(pkg);
    loadCommercialForPackage(pkg);
  };

  // Load technical products when a commercial product is selected
  const loadTechnical = useCallback(async (commercial) => {
    try {
      setTechnicalLoading(true);
      const res = await hierarchyService.getTechnicalProductsForCommercial(commercial.id);
      setTechnicalProducts(res.data.data.technicalProducts || []);
    } catch (err) {
      console.error('Error loading technical products:', err);
      showToast('Failed to load technical products', 'error');
    } finally {
      setTechnicalLoading(false);
    }
  }, []);

  const handleSelectCommercial = (commercial) => {
    if (technicalDirty) {
      // eslint-disable-next-line no-alert
      if (!window.confirm('You have unsaved changes. Save or discard before switching.')) return;
      setTechnicalDirty(false);
    }
    setSelectedCommercial(commercial);
    loadTechnical(commercial);
  };

  // Link commercial to package
  const handleLinkCommercial = async (commercialId) => {
    try {
      await hierarchyService.linkCommercial(selectedPackage.id, commercialId);
      showToast('Commercial product linked');
      await loadCommercialForPackage(selectedPackage);
      await loadPackages();
    } catch (err) {
      console.error('Error linking commercial:', err);
      showToast('Failed to link commercial product', 'error');
    }
  };

  // Unlink commercial from package
  const handleUnlinkCommercial = async (commercialId) => {
    if (selectedCommercial?.id === commercialId) {
      setSelectedCommercial(null);
      setTechnicalProducts(null);
    }
    try {
      await hierarchyService.unlinkCommercial(selectedPackage.id, commercialId);
      showToast('Commercial product unlinked');
      await loadCommercialForPackage(selectedPackage);
      await loadPackages();
    } catch (err) {
      console.error('Error unlinking commercial:', err);
      showToast('Failed to unlink commercial product', 'error');
    }
  };

  // Save technical product links
  const handleSaveTechnicalLinks = async (technicalProductIds) => {
    try {
      setTechnicalSaving(true);
      await hierarchyService.saveTechnicalLinks(selectedCommercial.id, technicalProductIds);
      showToast('Technical links saved');
      await loadTechnical(selectedCommercial);
      // Refresh commercial list so badge counts update
      await loadAllCommercial();
      if (selectedPackage) await loadCommercialForPackage(selectedPackage);
    } catch (err) {
      console.error('Error saving technical links:', err);
      showToast('Failed to save technical links', 'error');
    } finally {
      setTechnicalSaving(false);
    }
  };

  // Handle "Create new" — open modal for the appropriate product type
  const handleCreateNew = (productType) => {
    setModalProductType(productType);
    setShowModal(true);
  };

  // Called when ProductForm modal saves successfully
  const handleModalSuccess = async (savedProduct) => {
    setShowModal(false);
    setModalProductType(null);
    showToast(`${savedProduct.product_name} created`);

    if (savedProduct.product_type === 'PACKAGE') {
      await loadPackages();
    } else if (savedProduct.product_type === 'COMMERCIAL') {
      await loadAllCommercial();
      if (selectedPackage) {
        // Auto-link new commercial to current package
        try {
          await hierarchyService.linkCommercial(selectedPackage.id, savedProduct.id);
          await loadCommercialForPackage(selectedPackage);
          await loadPackages();
          showToast(`${savedProduct.product_name} created and linked`);
        } catch (err) {
          console.error('Auto-link failed:', err);
          await loadCommercialForPackage(selectedPackage);
        }
      }
    } else if (savedProduct.product_type === 'TECHNICAL' && selectedCommercial) {
      await loadTechnical(selectedCommercial);
    }
  };

  return (
    <div className="product-hierarchy">
      {!isAdmin && (
        <div className="product-hierarchy__readonly-banner">
          You are viewing in read-only mode. Contact an Admin to modify product links.
        </div>
      )}
      <div className="product-hierarchy__header">
        <h1 className="product-hierarchy__title">Product Hierarchy</h1>
        <p className="product-hierarchy__subtitle">
          Manage how packages, commercial products, and technical products are linked together.
        </p>
        <button className="product-hierarchy__refresh-btn" onClick={handleRefresh} title="Reset all selections and reload data">
          ↺ Refresh
        </button>
      </div>

      <div className="product-hierarchy__columns">
        <PackagesPanel
          packages={packages}
          selectedId={selectedPackage?.id}
          onSelect={handleSelectPackage}
          onCreatePackage={() => handleCreateNew('PACKAGE')}
          loading={packagesLoading}
          isAdmin={isAdmin}
        />

        <CommercialProductsPanel
          selectedPackage={selectedPackage}
          allProducts={allCommercial}
          linked={linkedCommercial}
          available={availableCommercial}
          selectedId={selectedCommercial?.id}
          onSelect={handleSelectCommercial}
          onLink={handleLinkCommercial}
          onUnlink={handleUnlinkCommercial}
          onCreateNew={() => handleCreateNew('COMMERCIAL')}
          loading={commercialLoading}
          isAdmin={isAdmin}
        />

        <TechnicalProductsPanel
          selectedCommercial={selectedCommercial}
          technicalProducts={technicalProducts}
          onSave={handleSaveTechnicalLinks}
          onCreateNew={() => handleCreateNew('TECHNICAL')}
          onDirtyChange={setTechnicalDirty}
          loading={technicalLoading}
          saving={technicalSaving}
          isAdmin={isAdmin}
        />
      </div>

      {/* Create Product Modal */}
      {showModal && (
        <ProductForm
          isModal
          lockedProductType={modalProductType}
          onSuccess={handleModalSuccess}
          onCancel={() => {
            setShowModal(false);
            setModalProductType(null);
          }}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <div className={`product-hierarchy__toast product-hierarchy__toast--${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default ProductHierarchy;
